/**
 * Nav - fixed glass-pill nav. Smooth-scrolls in-page anchors on the
 * home page; generates absolute /#anchor links on inner pages and blog
 * so nav works correctly from any URL.
 *
 * route values: "home" | "inner" | "blog"
 */
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

interface NavProps {
  route?: "home" | "blog" | "inner";
}

const LINKS: { id: string; label: string }[] = [
  { id: "services",   label: "Services" },
  { id: "defence",    label: "Defence-Ready" },
  { id: "industries", label: "Industries" },
  { id: "about",      label: "About" },
];

export default function Nav({ route = "home" }: NavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const href = (id: string) => route === "home" ? `#${id}` : `/#${id}`;
  const click = (id: string) => (e: React.MouseEvent) => {
    if (route !== "home") return; // let browser follow the link
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-40 px-6 py-6">
        <div
          className="liquid-glass rounded-full px-6 py-3 flex items-center justify-between max-w-5xl mx-auto"
          style={{
            background: scrolled ? "rgba(0,0,0,0.35)" : "rgba(255,255,255,0.01)",
            transition: "background 250ms ease",
          }}
        >
          <div className="flex items-center gap-8">
            <a href={route === "home" ? "#home" : "/"} aria-label="Paisums home">
              <img src="/logo-white.svg" alt="Paisums Technology" className="h-6 w-auto" />
            </a>
            <div className="hidden md:flex items-center gap-6">
              {LINKS.map((l) => (
                <a key={l.id} href={href(l.id)} onClick={click(l.id)}
                   className="text-white/80 hover:text-white transition-colors text-sm font-medium">
                  {l.label}
                </a>
              ))}
              <a href="/blog.html"
                 className={`text-sm font-medium transition-colors ${route === "blog" ? "text-white" : "text-white/80 hover:text-white"}`}>
                Insights
              </a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a href={route === "home" ? "#contact" : "/#contact"} onClick={click("contact")}
               className="liquid-glass rounded-full px-5 py-2 text-white text-sm font-medium hover:bg-white/5 transition-colors">
              Contact
            </a>
            <button className="md:hidden liquid-glass rounded-full p-2 text-white"
                    onClick={() => setOpen(true)} aria-label="Open menu">
              <Menu size={18} />
            </button>
          </div>
        </div>
      </nav>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl p-6 flex flex-col">
          <div className="flex justify-end">
            <button className="liquid-glass rounded-full p-3 text-white" onClick={() => setOpen(false)} aria-label="Close menu">
              <X size={18} />
            </button>
          </div>
          <div className="flex flex-col gap-2 mt-10">
            {LINKS.map((l) => (
              <a key={l.id} href={href(l.id)} onClick={click(l.id)}
                 className="font-instrument text-5xl text-white py-3 border-b border-white/10">{l.label}</a>
            ))}
            <a href="/blog.html" className="font-instrument text-5xl text-white py-3 border-b border-white/10">Insights</a>
            <a href={route === "home" ? "#contact" : "/#contact"} onClick={click("contact")}
               className="font-instrument text-5xl text-white py-3">Contact</a>
          </div>
        </div>
      )}
    </>
  );
}
